import { z } from 'zod';
import type { TeamMode } from './types';

export const zColor = z.enum(['red', 'blue', 'yellow', 'purple']);
export const zSkill = z.enum(['disrupt', 'shield']);
export const zPose = z.enum(['idle', 'climb', 'fall', 'victory']);
export const zRankTier = z.enum([
  'bronze',
  'silver',
  'gold',
  'platinum',
  'diamond',
  'master',
  'challenger',
]);

// ─── Client → Server ────────────────────────────────────────────────

export const zTeamMode = z.enum(['alternating', 'split-color', 'attack-defense']);

export const zQueueJoin = z.object({
  mode: z.enum(['solo', 'duo']).default('solo'),
  mmr: z.number().int().min(0),
  // duo only — the two same-team players share a partyId so the matchmaker
  // keeps them together, and a teamMode so it only pairs duos that picked the
  // same sub-mode.
  partyId: z.string().optional(),
  teamMode: zTeamMode.optional(),
});
export type QueueJoin = z.infer<typeof zQueueJoin>;

export const zQueueCancel = z.object({});
export type QueueCancel = z.infer<typeof zQueueCancel>;

export const zWarQueueJoin = z.object({ warId: z.string().min(1) });
export type WarQueueJoin = z.infer<typeof zWarQueueJoin>;

export interface WarNoOpponent {
  message: string;
}

export const zMatchTap = z.object({
  matchId: z.string(),
  step: z.number().int().min(0),
  color: zColor,
  t: z.number(),
});
export type MatchTap = z.infer<typeof zMatchTap>;

export const zMatchSkill = z.object({
  matchId: z.string(),
  skill: zSkill,
  t: z.number(),
});
export type MatchSkill = z.infer<typeof zMatchSkill>;

export const zMatchHeartbeat = z.object({
  matchId: z.string(),
  t: z.number(),
});
export type MatchHeartbeat = z.infer<typeof zMatchHeartbeat>;

export const zMatchLeave = z.object({
  matchId: z.string(),
});
export type MatchLeave = z.infer<typeof zMatchLeave>;

// ─── Server → Client ────────────────────────────────────────────────

/** A single equipped cosmetic, server-resolved so the client doesn't need a
 * second roundtrip to figure out what to render. `data` is the same slot-
 * specific JSON the admin authors (e.g. `{ tint: '#22D3EE' }` for a cape). */
export const zEquippedCosmetic = z.object({
  id: z.string(),
  name: z.string(),
  rarity: z.enum(['common', 'rare', 'epic', 'legendary']),
  iconUrl: z.string().nullable().optional(),
  data: z.record(z.unknown()),
});
export type EquippedCosmetic = z.infer<typeof zEquippedCosmetic>;

export const zEquippedSet = z.object({
  victoryShout: zEquippedCosmetic.nullable(),
  avatarFrame: zEquippedCosmetic.nullable(),
  // Endgame Finisher (slot endgameFx). `data.fxKey` selects the client win FX.
  endgameFx: zEquippedCosmetic.nullable(),
  // Match background is rendered on this player's lane and is shared with
  // rivals through match-found payloads. Older payloads fall back to null.
  matchBackground: zEquippedCosmetic.nullable().default(null),
});
export type EquippedSet = z.infer<typeof zEquippedSet>;

/** The equipped Character's render data — pose PNGs + win-effect tuning.
 * Sent in `match.found` so each side can render the rival's character. */
export const zCharacterSkin = z.object({
  id: z.string(),
  name: z.string(),
  idleUrl: z.string().nullable(),
  climbUrl: z.string().nullable(),
  fallUrl: z.string().nullable(),
  winUrl: z.string().nullable(),
  /** Full ordered animation frames per pose (base frame first). Present only
   * when a pose has >1 frame; the client plays them as a flipbook. */
  idleFrames: z.array(z.string()).nullable().optional(),
  climbFrames: z.array(z.string()).nullable().optional(),
  fallFrames: z.array(z.string()).nullable().optional(),
  winFrames: z.array(z.string()).nullable().optional(),
  stompPower: z.number(),
  tint: z.string().nullable(),
  /** Cosmetic tier — 2v2 shows the highest-rarity character of each lane's team. */
  rarity: z.enum(['common', 'rare', 'epic', 'legendary']).optional(),
});
export type CharacterSkin = z.infer<typeof zCharacterSkin>;

export const zPlayerSlim = z.object({
  id: z.string(),
  name: z.string(),
  mmr: z.number().int(),
  rank: zRankTier,
  avatarColor: z.enum(['red', 'blue']),
  avatarUrl: z.string().nullable().optional(),
  equipped: zEquippedSet.optional(),
  character: zCharacterSkin.nullable().optional(),
  /** Idle PNG of the equipped Character — avatar fallback when no photo. */
  characterIdleUrl: z.string().nullable().optional(),
  /** Equipped hold-shape skin. 2v2 uses each lane team's highest-rarity one. */
  holdSkin: z
    .object({ url: z.string(), rarity: z.enum(['common', 'rare', 'epic', 'legendary']) })
    .nullable()
    .optional(),
  /** Player's clan tag + emblem (emoji or /uploads url) — shown in-match so the
   * clan logo is visible during 1v1/2v2. Absent when the player isn't in a clan. */
  clanTag: z.string().nullable().optional(),
  clanEmblem: z.string().nullable().optional(),
});
export type PlayerSlim = z.infer<typeof zPlayerSlim>;

export const zQueueSearching = z.object({
  etaSeconds: z.number().int().min(0),
});
export type QueueSearching = z.infer<typeof zQueueSearching>;

export const zMatchFound = z.object({
  matchId: z.string(),
  you: zPlayerSlim,
  rival: zPlayerSlim,
  seq: z.object({
    you: z.array(zColor),
    rival: z.array(zColor),
  }),
  paths: z.object({
    you: z.array(z.number()),
    rival: z.array(z.number()),
  }),
  wallId: z.string(),
  wrTime: z.number(),
});
export type MatchFound = z.infer<typeof zMatchFound>;

export const zMatchCountdown = z.object({
  at: z.union([z.literal(3), z.literal(2), z.literal(1), z.literal(0)]),
});
export type MatchCountdown = z.infer<typeof zMatchCountdown>;

export const zMatchStart = z.object({
  startedAt: z.number(),
});
export type MatchStart = z.infer<typeof zMatchStart>;

export const zMatchStep = z.object({
  playerId: z.string(),
  step: z.number().int(),
  pose: zPose,
  t: z.number(),
});
export type MatchStep = z.infer<typeof zMatchStep>;

export const zMatchSkillFired = z.object({
  playerId: z.string(),
  skill: zSkill,
  t: z.number(),
  payload: z
    .object({
      reshuffleIndices: z.array(z.number().int()).optional(),
      // The ACTUAL post-shuffle colors at those indices. Sent alongside
      // `reshuffleIndices` for Disrupt so the targeted client can paint the
      // exact sequence the server now expects, instead of guessing with its
      // own random shuffle (which would desync taps and stall the match).
      reshuffleColors: z.array(zColor).optional(),
      // Shield cast within the disrupt flash window — these indices were
      // about to be repainted but the shield aborted the swap. Client clears
      // its flash + cancels the pending color-apply timer.
      cancelledDisruptIndices: z.array(z.number().int()).optional(),
      // Disrupt cast against a player who already had an active shield —
      // server skipped the whole flash + mutation. Client shows an absorb
      // bubble + a "ABSORBED" toast on the caster's side.
      absorbedBy: z.enum(['shield']).optional(),
    })
    .optional(),
});
export type MatchSkillFired = z.infer<typeof zMatchSkillFired>;

export const zMatchFall = z.object({
  playerId: z.string(),
  step: z.number().int(),
  t: z.number(),
});
export type MatchFall = z.infer<typeof zMatchFall>;

export const zRankSnap = z.object({
  rank: zRankTier,
  division: z.number().int().min(1).max(4),
  stars: z.number().int().min(0),
});
export type RankSnap = z.infer<typeof zRankSnap>;

export const zRankProgression = z.object({
  before: zRankSnap,
  after: zRankSnap,
  starsDelta: z.number().int(),
  promoted: z.boolean(),
  demoted: z.boolean(),
});
export type RankProgressionPayload = z.infer<typeof zRankProgression>;

/** The winner's equipped Victory Shout, resolved server-side. Shown on BOTH
 * result screens — the loser sees the winner taunting them. */
export const zMatchShout = z.object({
  text: z.string(),
  rarity: z.enum(['common', 'rare', 'epic', 'legendary']),
});
export type MatchShout = z.infer<typeof zMatchShout>;

/** Per-player end-of-match performance, computed server-side from the tap log.
 * `timeMs` = ms from race start to the player's last correct tap (their finish
 * time if they completed). Drives the "battle stats" panel on the result screen. */
export const zPlayerStats = z.object({
  accuracy: z.number().int().min(0).max(100),
  maxCombo: z.number().int().min(0),
  timeMs: z.number().int().min(0),
});
export type PlayerStats = z.infer<typeof zPlayerStats>;

export const zMatchOver = z.object({
  winnerId: z.string(),
  finalSteps: z.object({ you: z.number().int(), rival: z.number().int() }),
  durationMs: z.number().int(),
  // Real per-player stats (you/rival from the receiver's perspective). Optional
  // for back-compat with older clients / team mode.
  stats: z.object({ you: zPlayerStats, rival: zPlayerStats }).optional(),
  mmrDelta: z.object({ you: z.number().int(), rival: z.number().int() }),
  rewards: z.object({ coins: z.number().int(), xp: z.number().int() }),
  // Rank-stars progression for the receiving player. Drives the post-match
  // "RANK UP" / demotion overlay on the client.
  rankProgression: zRankProgression.optional(),
  // Winner's equipped Victory Shout (null if none / winner is a bot).
  winnerShout: zMatchShout.nullable().optional(),
});
export type MatchOver = z.infer<typeof zMatchOver>;

export const zMatchDesync = z.object({
  expectedStep: z.number().int(),
  gotStep: z.number().int(),
});
export type MatchDesync = z.infer<typeof zMatchDesync>;

export const zErrorEvent = z.object({
  code: z.string(),
  message: z.string(),
});
export type ErrorEvent = z.infer<typeof zErrorEvent>;

// ─── 2v2 Team match ─────────────────────────────────────────────────
// A member within a team. Extends PlayerSlim with the per-mode role data the
// client needs to gate input and render the right HUD.
export const zTeamMember = zPlayerSlim.extend({
  // Position within the team (0 or 1). Drives alternating turn order and the
  // attack-defense role assignment (0 = climber, 1 = attacker).
  order: z.number().int().min(0),
  // split-color: the 2 colors this member is responsible for tapping.
  ownedColors: z.array(zColor).optional(),
  // attack-defense: resolved role.
  role: z.enum(['climber', 'attacker']).optional(),
});
export type TeamMember = z.infer<typeof zTeamMember>;

export const zTeamMatchFound = z.object({
  matchId: z.string(),
  teamMode: zTeamMode,
  // Receiving player's lane: 'a' = left, 'b' = right.
  yourSide: z.enum(['a', 'b']),
  youId: z.string(),
  yourTeam: z.array(zTeamMember), // you + teammate
  rivalTeam: z.array(zTeamMember),
  seq: z.object({ you: z.array(zColor), rival: z.array(zColor) }),
  paths: z.object({ you: z.array(z.number()), rival: z.array(z.number()) }),
  wallId: z.string(),
  wrTime: z.number(),
});
export type TeamMatchFound = z.infer<typeof zTeamMatchFound>;

// A team's climber advanced. Carries the team-shared combo + who tapped + (for
// alternating) whose turn is next so every client can gate input identically.
export const zTeamMatchStep = z.object({
  side: z.enum(['a', 'b']),
  step: z.number().int(),
  pose: zPose,
  combo: z.number().int(),
  byPlayerId: z.string(),
  nextTurnId: z.string().nullable().optional(),
  t: z.number(),
});
export type TeamMatchStep = z.infer<typeof zTeamMatchStep>;

export const zTeamMatchFall = z.object({
  side: z.enum(['a', 'b']),
  step: z.number().int(),
  combo: z.number().int(),
  byPlayerId: z.string(),
  nextTurnId: z.string().nullable().optional(),
  t: z.number(),
});
export type TeamMatchFall = z.infer<typeof zTeamMatchFall>;

export const zTeamSkillFired = z.object({
  side: z.enum(['a', 'b']), // the team that CAST the skill
  casterId: z.string(),
  skill: zSkill,
  // simultaneous co-cast bonus active (alternating mode).
  enhanced: z.boolean().optional(),
  t: z.number(),
  payload: z
    .object({
      reshuffleIndices: z.array(z.number().int()).optional(),
      reshuffleColors: z.array(zColor).optional(),
      cancelledDisruptIndices: z.array(z.number().int()).optional(),
      absorbedBy: z.enum(['shield']).optional(),
    })
    .optional(),
});
export type TeamSkillFired = z.infer<typeof zTeamSkillFired>;

export const zTeamMatchOver = z.object({
  winnerSide: z.enum(['a', 'b']),
  // From the receiving player's perspective.
  finalSteps: z.object({ you: z.number().int(), rival: z.number().int() }),
  durationMs: z.number().int(),
  mmrDelta: z.number().int(),
  rewards: z.object({ coins: z.number().int(), xp: z.number().int() }),
  rankProgression: zRankProgression.optional(),
  winnerShout: zMatchShout.nullable().optional(),
  // Every winning-team member's shout (2v2). The loser's result screen cycles
  // through these one at a time; `winnerShout` stays as the first for back-compat.
  winnerShouts: z.array(zMatchShout).optional(),
});
export type TeamMatchOver = z.infer<typeof zTeamMatchOver>;

// ─── Event name constants (for type-safe socket.on/emit) ────────────

export const EV = {
  // C→S
  QUEUE_JOIN: 'queue.join',
  QUEUE_CANCEL: 'queue.cancel',
  // C→S: clan-war matchmaking (head-to-head vs the opposing clan; no bots).
  WAR_QUEUE_JOIN: 'war.queue.join',
  WAR_QUEUE_CANCEL: 'war.queue.cancel',
  MATCH_TAP: 'match.tap',
  MATCH_SKILL: 'match.skill',
  MATCH_HEARTBEAT: 'match.heartbeat',
  MATCH_LEAVE: 'match.leave',
  // C→S: client signals its match scene is loaded/rendered. The server holds the
  // countdown until both players are ready (or a timeout) so the race starts in
  // sync on both screens.
  MATCH_READY: 'match.ready',
  NET_PING: 'net.ping',
  // S→C
  NET_PONG: 'net.pong',
  QUEUE_SEARCHING: 'queue.searching',
  MATCH_FOUND: 'match.found',
  MATCH_COUNTDOWN: 'match.countdown',
  MATCH_START: 'match.start',
  MATCH_STEP: 'match.step',
  MATCH_SKILL_FIRED: 'match.skill.fired',
  MATCH_FALL: 'match.fall',
  MATCH_OVER: 'match.over',
  MATCH_DESYNC: 'match.desync',
  ERROR: 'error',
  APP_MAINTENANCE: 'app.maintenance',
  // 2v2 team match (S→C). Countdown/start/tap/skill/leave reuse the 1v1
  // events above — only found/step/fall/skill-fired/over carry team shape.
  TEAM_MATCH_FOUND: 'team.found',
  TEAM_MATCH_STEP: 'team.step',
  TEAM_MATCH_FALL: 'team.fall',
  TEAM_SKILL_FIRED: 'team.skill.fired',
  TEAM_MATCH_OVER: 'team.over',
  // S→C: clan-war queue found no opposing-clan member in time → stop searching.
  WAR_NO_OPPONENT: 'war.no_opponent',
} as const;

// ── Social realtime payloads ─────────────────────────────────────────
export interface PresenceUpdate {
  userId: string;
  online: boolean;
}

export interface ChatSenderSlim {
  id: string;
  displayName: string;
  avatarUrl: string | null;
  avatarColor: 'red' | 'blue';
  rank: 'bronze' | 'silver' | 'gold' | 'platinum' | 'diamond' | 'master' | 'challenger';
  frame?: AvatarFrameSpec | null;
  characterIdleUrl?: string | null;
}

export interface ChatGlobalMessage {
  id: string;
  text: string;
  createdAt: string;
  sender: ChatSenderSlim;
}

export interface ChatClanMessage {
  id: string;
  text: string;
  createdAt: string;
  clanId: string;
  sender: ChatSenderSlim;
}

export interface ChatDmMessage {
  id: string;
  text: string;
  fromId: string;
  toId: string;
  createdAt: string;
}

// ── Room (private lobby, 4 slots / 2 teams) ─────────────────────────
export type RoomSide = 'A' | 'B';
export type RoomStatus = 'lobby' | 'searching' | 'starting' | 'active' | 'closed';
export type RoomMode =
  | 'empty'
  | 'quick'
  | 'solo-duel'
  | 'duo-rank'
  | 'team-duel'
  | 'imbalanced';

export interface AvatarFrameSpec {
  border?: string | null;
  glow?: string | null;
}

export interface RoomSlotDto {
  slot: 1 | 2 | 3 | 4;
  side: RoomSide;
  ready: boolean;
  joinedAt: number | null;
  user: {
    id: string;
    displayName: string;
    avatarUrl: string | null;
    avatarColor: 'red' | 'blue';
    rank: string;
    rankDivision: number;
    mmr: number;
    frame?: AvatarFrameSpec | null;
    characterIdleUrl?: string | null;
  } | null;
}

export interface RoomDto {
  code: string;
  hostId: string;
  status: RoomStatus;
  mode: RoomMode;
  teamMode: TeamMode;
  createdAt: number;
  slots: RoomSlotDto[];
  activeMatchId?: string;
}

export interface RoomMatchStartPayload {
  mode: RoomMode;
  matchId?: string;
  teamMode?: TeamMode;
  room: RoomDto;
}

export interface ServerToClientEvents {
  [EV.QUEUE_SEARCHING]: (payload: QueueSearching) => void;
  [EV.MATCH_FOUND]: (payload: MatchFound) => void;
  [EV.MATCH_COUNTDOWN]: (payload: MatchCountdown) => void;
  [EV.MATCH_START]: (payload: MatchStart) => void;
  [EV.MATCH_STEP]: (payload: MatchStep) => void;
  [EV.MATCH_SKILL_FIRED]: (payload: MatchSkillFired) => void;
  [EV.MATCH_FALL]: (payload: MatchFall) => void;
  [EV.MATCH_OVER]: (payload: MatchOver) => void;
  [EV.MATCH_DESYNC]: (payload: MatchDesync) => void;
  [EV.ERROR]: (payload: ErrorEvent) => void;
  [EV.APP_MAINTENANCE]: (payload: { message: string }) => void;
  [EV.WAR_NO_OPPONENT]: (payload: WarNoOpponent) => void;
  [EV.NET_PONG]: (payload: { t: number }) => void;
  [EV.TEAM_MATCH_FOUND]: (payload: TeamMatchFound) => void;
  [EV.TEAM_MATCH_STEP]: (payload: TeamMatchStep) => void;
  [EV.TEAM_MATCH_FALL]: (payload: TeamMatchFall) => void;
  [EV.TEAM_SKILL_FIRED]: (payload: TeamSkillFired) => void;
  [EV.TEAM_MATCH_OVER]: (payload: TeamMatchOver) => void;
  'presence:update': (payload: PresenceUpdate) => void;
  'chat:global': (payload: ChatGlobalMessage) => void;
  'chat:clan': (payload: ChatClanMessage) => void;
  'chat:cleared': () => void;
  'chat:dm': (payload: ChatDmMessage) => void;
  'room:update': (payload: RoomDto) => void;
  'room:chat': (payload: RoomChatMessage) => void;
  'room:kicked': (payload: { code: string }) => void;
  'room:matchStart': (payload: RoomMatchStartPayload) => void;
  'friend:request': (payload: { from: FriendEntrySlim }) => void;
  'friend:accepted': (payload: { friend: FriendEntrySlim }) => void;
  'room:invite': (payload: RoomInvitePayload) => void;
  'clan:notif': (payload: ClanNotifPayload) => void;
  /** Silent nudge to re-fetch clan state (no toast/badge) — e.g. someone
   * cancelled a join request, so officers' request lists should update live. */
  'clan:refresh': () => void;
}

/** A live clan notification pushed to the recipient's `user:<id>` room. The
 * full inbox (with ids + read state) is fetched over REST; this just drives the
 * realtime toast + unread badge. `data` is event-specific (see ClanNotifType). */
export interface ClanNotifPayload {
  type:
    | 'join_request'
    | 'join_approved'
    | 'join_rejected'
    | 'kicked'
    | 'role_changed'
    | 'war_scheduled'
    | 'war_open'
    | 'war_ended'
    | 'emblem_awarded';
  data: Record<string, unknown>;
  createdAt: number;
}

export interface RoomInvitePayload {
  code: string;
  from: {
    id: string;
    displayName: string;
    avatarColor: 'red' | 'blue';
    avatarUrl: string | null;
    characterIdleUrl?: string | null;
  };
  expiresAt: number;
}

export interface FriendEntrySlim {
  userId: string;
  displayName: string;
  avatarUrl: string | null;
  avatarColor: 'red' | 'blue';
  rank: 'bronze' | 'silver' | 'gold' | 'platinum' | 'diamond' | 'master' | 'challenger';
  rankDivision: number;
  mmr: number;
  online: boolean;
  sinceMs: number;
  frame?: AvatarFrameSpec | null;
  characterIdleUrl?: string | null;
}

export interface ClientToServerEvents {
  [EV.QUEUE_JOIN]: (payload: QueueJoin) => void;
  [EV.QUEUE_CANCEL]: (payload: QueueCancel) => void;
  [EV.WAR_QUEUE_JOIN]: (payload: WarQueueJoin) => void;
  [EV.WAR_QUEUE_CANCEL]: (payload: QueueCancel) => void;
  [EV.MATCH_TAP]: (payload: MatchTap) => void;
  [EV.MATCH_SKILL]: (payload: MatchSkill) => void;
  [EV.MATCH_HEARTBEAT]: (payload: MatchHeartbeat) => void;
  [EV.MATCH_LEAVE]: (payload: MatchLeave) => void;
  [EV.MATCH_READY]: (payload?: { matchId?: string }) => void;
  [EV.NET_PING]: (payload: { t: number }) => void;
  'chat:global': (payload: { text: string }) => void;
  'chat:clan': (payload: { text: string }) => void;
  'chat:dm': (payload: { toId: string; text: string }) => void;
  'room:chat': (payload: { text: string }) => void;
}

/** Ephemeral team-lobby chat message (not persisted). */
export interface RoomChatMessage {
  id: string;
  text: string;
  createdAt: string;
  sender: {
    id: string;
    displayName: string;
    avatarUrl: string | null;
    avatarColor: 'red' | 'blue';
  };
}
